import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhoneCall, Robot, Waveform, Headset, Users, ArrowRight, Sparkle, ShieldCheck } from "@phosphor-icons/react";

const FEATURES = [
    { icon: PhoneCall, title: "One-click browser softphone", body: "Call any number straight from your workspace — no hardware, no desk phone." },
    { icon: Robot, title: "AI call assistant", body: "Automatic transcripts, summaries & sentiment on every conversation." },
    { icon: Waveform, title: "Recording & live monitoring", body: "Record calls and let supervisors listen in to coach in real time." },
    { icon: Users, title: "Salesforce screen-pop", body: "The right customer record opens automatically — every call auto-logged." },
    { icon: ShieldCheck, title: "Queues, routing & transfers", body: "Route inbound calls to the right agent and warm-transfer in a tap." },
];

/**
 * Upsell surface shown to WhatsApp-Standalone workspaces so they discover the
 * voice / call-center add-on. `variant`: "banner" (Overview) | "panel" (Settings tab).
 */
export default function CallingUpsellCard({ variant = "banner" }) {
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const [busy, setBusy] = useState(false);
    const compact = variant === "banner";
    const trialUsed = !!user?.calling_trial_used;

    const startTrial = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/billing/start-calling-trial");
            await refresh();
            toast.success(`Your ${data.days || 7}-day calling trial is live — the dialer is now unlocked!`);
            navigate("/");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start your trial. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="calling-upsell"
            className="relative overflow-hidden rounded-3xl border border-brand/20 bg-ink text-white shadow-xl"
        >
            {/* Ambient brand glow */}
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />

            <div className="relative grid gap-8 p-8 md:p-10 lg:grid-cols-5">
                {/* Left — pitch */}
                <div className="lg:col-span-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-brand">
                        <Sparkle size={13} weight="fill" /> Add-on · Call Center
                    </div>
                    <h2 className="mt-4 font-heading text-3xl font-bold leading-tight sm:text-4xl">
                        You're on WhatsApp.<br />Now pick up the phone.
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-white/70">
                        You already reach customers where they chat. Add voice to the same workspace
                        and close deals faster — calls, recordings and AI summaries living right beside
                        your WhatsApp threads.
                    </p>

                    <div className="mt-5 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand text-white">
                            <PhoneCall size={22} weight="fill" />
                        </div>
                        <div>
                            <div className="font-heading text-sm font-semibold">Teams that add voice reply 3× faster</div>
                            <div className="text-xs text-white/60">Voice + WhatsApp, one unified customer view.</div>
                        </div>
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        {!trialUsed ? (
                            <button
                                data-testid="calling-trial-cta"
                                onClick={startTrial}
                                disabled={busy}
                                className="group inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 font-heading text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-60"
                            >
                                {busy ? "Starting…" : "Try calling free for 7 days"}
                                <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                            </button>
                        ) : (
                            <button
                                data-testid="calling-upsell-cta"
                                onClick={() => navigate("/billing#calling-plans")}
                                className="group inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 font-heading text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover"
                            >
                                See calling plans
                                <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                            </button>
                        )}
                        <button
                            data-testid="calling-upsell-plans"
                            onClick={() => navigate("/billing#calling-plans")}
                            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 text-sm font-semibold text-white/90 transition-colors hover:bg-white/10"
                        >
                            {trialUsed ? "Talk to sales" : "See plans"}
                        </button>
                        <a
                            data-testid="calling-upsell-sales"
                            href="mailto:sales@inspiroworks.com?subject=Add%20Call%20Center%20to%20my%20workspace"
                            className="text-sm font-semibold text-white/60 underline decoration-brand/60 underline-offset-4 transition-colors hover:text-white"
                        >
                            Talk to sales
                        </a>
                    </div>
                    {!trialUsed
                        ? <p className="mt-2.5 text-xs text-white/50" data-testid="calling-trial-note">No card required · full features · your WhatsApp stays exactly as it is.</p>
                        : <p className="mt-2.5 text-xs text-white/50" data-testid="calling-trial-used-note">Your free calling trial has been used. Pick a plan to switch calling back on.</p>}
                </div>

                {/* Right — capability grid */}
                <div className={`grid gap-3 lg:col-span-3 ${compact ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
                    {FEATURES.map((f) => (
                        <div
                            key={f.title}
                            className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 transition-colors hover:border-brand/40 hover:bg-white/[0.07]"
                        >
                            <f.icon size={22} weight="duotone" className="text-brand" />
                            <div className="mt-2 font-heading text-sm font-semibold">{f.title}</div>
                            <div className="mt-1 text-xs leading-relaxed text-white/60">{f.body}</div>
                        </div>
                    ))}
                    <div className="hidden items-center justify-center rounded-2xl border border-dashed border-white/15 p-4 text-center sm:flex">
                        <div>
                            <Headset size={24} weight="duotone" className="mx-auto text-white/50" />
                            <div className="mt-1.5 text-xs font-medium text-white/60">Set up in minutes with Twilio, Plivo or Exotel</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
